import { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { SOURCES } from "../lib/constants";

const empty = {
  name: "",
  phone: "",
  email: "",
  company: "",
  source: "instagram",
  deal_value: "",
  social_handle: "",
  notes: "",
};

export default function AddLeadModal({ open, onOpenChange }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const qc = useQueryClient();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", {
        ...form,
        deal_value: parseFloat(form.deal_value) || 0,
      });
      toast.success("Lead added");
      qc.invalidateQueries();
      setForm(empty);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-md" data-testid="add-lead-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Add a new lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="lead-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Phone (E.164)</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+15551234567" data-testid="lead-phone-input" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => set("email", e.target.value)} type="email" data-testid="lead-email-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => set("source", v)}>
                <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {SOURCES.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Deal value ($)</Label>
              <Input value={form.deal_value} onChange={(e) => set("deal_value", e.target.value)} type="number" data-testid="lead-value-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Company</Label>
              <Input value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="lead-company-input" />
            </div>
            <div className="space-y-2">
              <Label>Social handle</Label>
              <Input value={form.social_handle} onChange={(e) => set("social_handle", e.target.value)} placeholder="@handle" data-testid="lead-handle-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="lead-notes-input" />
          </div>
          <DialogFooter>
            <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90" disabled={loading} data-testid="save-lead-button">
              {loading ? "Saving..." : "Add lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
